import React, { useState, useEffect } from 'react';
import './report-options-form.scss';
import getReportTypeOptionsConfig from './report-type-options-config';
import DeprecatedIcon from '../../assets/svg-icons';
import {
  mapScheduledReportToFormValues,
  mapFormValuesToRequest,
  getProducts,
  isAllProductsSelectedByValues,
  getCurrentDayOfWeek,
  showFinancialYearRadioButton,
  ScheduledReport,
  FormValues,
} from './report-options-form-mappers';
import {
  validationSchema,
  renewalPeriodValidationSchema,
  reportingDayOfWeekValidationSchema,
  createProcessedDatesValidationSchema,
} from './report-options-form-validation-schema';
import { Form } from '@ampol/design-system';
import { DATE_FORMAT } from 'utilities/constants';
import SuffixWrapper from 'components/suffix-wrapper/suffix-wrapper';
import dayjs from 'dayjs';

interface ReportOptionsFormProps {
  isEdit: any;
  scheduledReport: ScheduledReport;
  reportTypeId: any;
  reportName: any;
  reportDescription: any;
  onSubmit: any;
  formId: any;
  isDisabled: any;
  emailId: any;
  onStateChange?: any;
}
/**
 * Report options form for creating/updating scheduled report
 * @returns JSX Element
 */
const ReportOptionsForm = ({
  isEdit,
  scheduledReport,
  reportTypeId,
  reportName,
  reportDescription,
  onSubmit,
  formId,
  isDisabled,
  emailId,
  onStateChange,
}: ReportOptionsFormProps) => {
  const [formConfig, setFormConfig] = useState(
    !isEdit && reportTypeId
      ? getReportTypeOptionsConfig().find((x) => x.reportTypeId === parseInt(reportTypeId))
      : undefined
  );
  const [isSchedule, setIsSchedule] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState('Monthly');
  const [formValidationSchema, setFormValidationSchema] = useState({});

  // Filter products: EV only for Account Transaction Detail Report (reportTypeId 3)
  const availableProducts = getProducts().filter((product) => {
    if (product.value === 'ev') {
      return parseInt(reportTypeId) === 3;
    }
    return true;
  });

  const defaultDateFrom = formConfig?.filters?.minFromDate ? dayjs().startOf('year').format(DATE_FORMAT) : undefined;

  const outputFormats = formConfig?.outputFormats.map((format) => {
    return {
      label: format,
      value: format,
    };
  });

  const dayOfWeek = getCurrentDayOfWeek();

  const [formValues, setFormValues] = useState<FormValues>({
    reportName: reportName,
    reportDescription: reportDescription,
    outputFormat: outputFormats ? outputFormats[0].value : undefined,
    createSchedule: 'no',
    scheduleFrequency: 'Monthly',
    reportingDayOfWeek: dayOfWeek,
    reportingDay: dayOfWeek,
    scheduleReportingPeriod: 'CalendarMonth',
    emailAsAttachment: false,
    emailSubject: `Card Portal: ${reportName}`,
    emailTo: emailId,
    processedDateFrom: defaultDateFrom,
  });

  useEffect(() => {
    if (isEdit && scheduledReport) {
      setFormValues(mapScheduledReportToFormValues(scheduledReport));
      setIsSchedule(!!scheduledReport.reportSchedule?.scheduleFrequency);
      setScheduleFrequency(scheduledReport.reportSchedule?.scheduleFrequency ?? 'Monthly');
    }
  }, [scheduledReport?.reportScheduleId]);

  const scheduleReportingPeriodOptions = [
    {
      label: 'Prior Day',
      value: 'PriorDay',
      scheduleFrequency: ['Daily'],
    },
    {
      label: 'Prior Week',
      value: 'PriorWeek',
      scheduleFrequency: ['Weekly'],
    },
    {
      label: 'Calendar Month To Date',
      value: 'CalendarMonthToDate',
      scheduleFrequency: ['Daily', 'Weekly'],
    },
    {
      label: 'Calendar Month',
      value: 'CalendarMonth',
      scheduleFrequency: ['Monthly'],
    },
    {
      label: 'Calendar Year To Date',
      value: 'CalendarYearToDate',
      scheduleFrequency: ['Monthly'],
    },
    /* eslint-disable indent */
    ...(showFinancialYearRadioButton(formConfig ?? {})
      ? [
          {
            label: 'Current Financial Year To Date',
            value: 'CurrentFinancialYearToDate',
            scheduleFrequency: ['Monthly'],
          },
        ]
      : []),
    /* eslint-enable indent */
  ];

  useEffect(() => {
    setFormConfig(getReportTypeOptionsConfig().find((x) => x.reportTypeId === parseInt(reportTypeId)));
  }, [reportTypeId]);

  useEffect(() => {
    let schema = validationSchema;

    if (!isSchedule && formConfig?.filters?.showProcessedDateSection) {
      schema = schema.concat(createProcessedDatesValidationSchema(getMinMaxDates())) as any;
    }

    if (isSchedule && scheduleFrequency === 'Weekly') {
      schema = schema.concat(reportingDayOfWeekValidationSchema);
    }

    if (formConfig?.filters?.showCardRenewalPeriod) {
      schema = schema.concat(renewalPeriodValidationSchema);
    }

    setFormValidationSchema(schema);
  }, [isSchedule, scheduleFrequency, formConfig]);

  const onSubmitClick = (values: any) => {
    if (values.cardNumber) {
      values.cardNumber = values.cardNumber.replace(/ /g, ''); // remove spaces from card number
    }

    const request = mapFormValuesToRequest(scheduledReport, reportTypeId, values);

    if (isEdit && scheduledReport) {
      onSubmit(request, scheduledReport.reportScheduleId);
    } else {
      onSubmit(request);
    }
  };

  /* eslint-disable indent */
  const onFormChange = (values: any, fieldName: any) => {
    if (fieldName === 'allProducts') {
      toggleProductsSelections(values);
    } else if (fieldName === 'createSchedule') {
      setIsSchedule(values.createSchedule === 'yes');
      if (values.createSchedule === 'yes') {
        setFormValues({
          ...values,
          scheduleFrequency: 'Monthly',
          scheduleReportingPeriod: 'CalendarMonth',
          emailTo: emailId,
        });
      } else if (values.createSchedule === 'no' && values.emailTo === '') {
        setFormValues({ ...values, emailTo: emailId });
      }
    } else if (fieldName === 'scheduleFrequency') {
      setScheduleFrequency(values.scheduleFrequency);
      if (values.scheduleFrequency) {
        const options = formConfig?.filters?.showMonthToDateCardLimit
          ? [{ label: 'Calendar Month To Date', value: 'CalendarMonthToDate' }]
          : scheduleReportingPeriodOptions.filter((opt) =>
              opt.scheduleFrequency.some((frq) => frq === values.scheduleFrequency)
            );
        setFormValues({ ...values, scheduleReportingPeriod: options[0].value });
      }
    } else {
      setFormValues({
        ...values,
        allProducts: formConfig?.filters?.showProductsSection
          ? isAllProductsSelectedByValues(values, availableProducts)
          : undefined,
      });
    }
  };
  /* eslint-enable indent */

  const toggleProductsSelections = (values: any) => {
    const updatedValues = {
      ...values,
    };

    // Toggle all available products based on allProducts checkbox
    availableProducts.forEach((product) => {
      updatedValues[product.value] = values.allProducts;
    });

    setFormValues(updatedValues);
  };

  const getMinMaxDates = () => {
    const maxDate = dayjs();
    const minDate = formConfig?.filters?.minFromDate
      ? dayjs(formConfig?.filters?.minFromDate)
      : maxDate.subtract(1, 'year').startOf('day');

    return { minDate, maxDate };
  };

  /**
   * Determines if a date should be disabled in the DatePicker
   *
   * @param {typeof dayjs} current date being evaludated
   */
  const disabledDate = (current: any) => {
    const { minDate, maxDate } = getMinMaxDates();

    return current < minDate || current > maxDate;
  };

  return (
    <>
      {!formConfig && (
        <div className='report-options-form__loading-state'>
          <div className='report-options-form__form-section report-options-form__form-section--full' />
          <div className='report-options-form__form-section report-options-form__form-section--full' />
          <div className='report-options-form__form-section' />
          <div className='report-options-form__form-section report-options-form__form-section--double' />
          <div className='report-options-form__form-section' />
        </div>
      )}

      {formConfig && (
        <Form
          id={formId}
          schema={formValidationSchema}
          defaultValues={
            formConfig.filters.showMonthToDateCardLimit
              ? { ...formValues, scheduleReportingPeriod: 'CalendarMonthToDate' }
              : formValues
          }
          onSubmit={onSubmitClick}
          onChange={onFormChange}
          onStateChange={onStateChange}
        >
          <div className='report-options-form__options-container'>
            <div className='report-options-form__form-section report-options-form__form-section--full'>
              <div className='report-options-form__form-row'>
                <div className='report-options-form__form-label'>Report Name:</div>
                <div className='report-options-form__form-value'>{reportName}</div>
              </div>
            </div>
            <div className='report-options-form__form-section report-options-form__form-section--full report-options-form__form-section--createschedule'>
              <h3>Create Schedule?</h3>
              <Form.RadioGroup
                label='Create Schedule'
                labelType={'hidden'}
                color='black'
                name='createSchedule'
                radioType='radio'
                direction='row'
                disabledType='radio'
                disabled={isDisabled}
                values={[
                  {
                    label: 'No',
                    value: 'no',
                    tooltip: 'Run report once',
                  },
                  {
                    label: 'Yes',
                    value: 'yes',
                    tooltip: 'Create recurring schedule',
                  },
                ]}
              />
            </div>
            {formConfig.filters.showFilters && (
              <div className='report-options-form__form-section report-options-form__form-section--full'>
                <h3>Filters</h3>
                {formConfig.filters.showProcessedDateSection && (
                  <Form.Condition fieldName='createSchedule' condition={(c) => c === 'no'}>
                    <div>
                      <div className='report-options-form__filter-sub-heading'>Processed Date</div>
                      <div className='report-options-form__fields'>
                        <div className='report-options-form__filter-date'>
                          <Form.DatePicker
                            placeholder='(dd/mm/yyyy)'
                            name='processedDateFrom'
                            label='From'
                            inputSuffix={
                              <SuffixWrapper>
                                <DeprecatedIcon iconName='calender' />
                              </SuffixWrapper>
                            }
                            disabled={isDisabled}
                            getPopupContainer={() => document.body}
                            disabledDate={disabledDate}
                          />
                        </div>
                        <div className='report-options-form__filter-date'>
                          <Form.DatePicker
                            placeholder='(dd/mm/yyyy)'
                            name='processedDateTo'
                            label='To'
                            ariaLabel='To'
                            inputSuffix={
                              <SuffixWrapper>
                                <DeprecatedIcon iconName='calender' />
                              </SuffixWrapper>
                            }
                            disabled={isDisabled}
                            getPopupContainer={() => document.body}
                            disabledDate={disabledDate}
                          />
                        </div>
                      </div>
                    </div>
                  </Form.Condition>
                )}
                {formConfig.filters.showCardDriverDetailsSection && (
                  <>
                    <div className='report-options-form__filter-sub-heading'>Card/Driver Details</div>
                    <div className='report-options-form__fields'>
                      {formConfig.filters.showCostCentre && (
                        <Form.Input
                          label={'Cost Centre'}
                          name={'costCentre'}
                          disabled={isDisabled}
                          className={'report-options-form__uppercase'}
                        />
                      )}
                      {formConfig.filters.showDomesticNumber && (
                        <Form.Input
                          label={'Domestic Number'}
                          name={'domesticNumber'}
                          disabled={isDisabled}
                          className={'report-options-form__uppercase'}
                        />
                      )}
                      {formConfig.filters.showCardNumber && (
                        <Form.Input label={'Card Number'} name={'cardNumber'} disabled={isDisabled} />
                      )}
                      {formConfig.filters.showRegistration && (
                        <Form.Input
                          label={'Registration'}
                          name={'registration'}
                          disabled={isDisabled}
                          className={'report-options-form__uppercase'}
                        />
                      )}

                      {formConfig.filters.showCardRenewalPeriod && (
                        <Form.DropDown
                          label='Card Expiry Period'
                          name='cardRenewalPeriod'
                          disabled={isDisabled}
                          options={[
                            {
                              label: 'Card Expiring Next Month',
                              value: 'nextMth',
                            },
                            {
                              label: 'Card Expiring Next 3 Months',
                              value: 'next3mths',
                            },
                            {
                              label: 'Card Expiring Next 6 Months',
                              value: 'next6mths',
                            },
                          ]}
                        />
                      )}
                    </div>
                  </>
                )}
                {formConfig.filters.showMonthToDateCardLimitSection && (
                  <>
                    <div className='report-options-form__filter-sub-heading'>Month to Date Card Limit</div>
                    <div className='report-options-form__fields'>
                      {formConfig.filters.showMonthToDateCardLimit && (
                        <Form.DropDown
                          label='Month to Date Card Limit'
                          name='monthToDateCardLimit'
                          disabled={isDisabled}
                          options={[
                            {
                              label: 'Equal or greater than 50%',
                              value: 'EqualToOrGreaterThan50',
                            },
                            {
                              label: 'Equal or greater than 70%',
                              value: 'EqualToOrGreaterThan70',
                            },
                            {
                              label: 'Equal or greater than 80%',
                              value: 'EqualToOrGreaterThan80',
                            },
                          ]}
                        />
                      )}
                    </div>
                  </>
                )}

                {formConfig.filters.showOtherFiltersSection && (
                  <>
                    <div className='report-options-form__filter-sub-heading'>Other Filters</div>
                    <div className='report-options-form__fields'>
                      {formConfig.filters.showMinNumCarWash && (
                        <Form.Input
                          label={'Min Number of Car Wash'}
                          name={'minNumberOfCarWashes'}
                          disabled={isDisabled}
                        />
                      )}
                      {formConfig.filters.showMinNumFills && (
                        <Form.Input label={'Min Number of Fills'} name={'minNumberOfFills'} disabled={isDisabled} />
                      )}
                      {formConfig.filters.showVolumeLitres && (
                        <Form.Input label={'Volume (Litres)'} name={'minTransactionVolume'} disabled={isDisabled} />
                      )}
                      {formConfig.filters.showLocation && (
                        <Form.Input label={'Location'} name={'locations'} disabled={isDisabled} />
                      )}
                      {formConfig.filters.showTransactionMoreThan && (
                        <Form.Input
                          label={'Nonfuel Transaction $ More Than'}
                          name={'minTransactionAmount'}
                          disabled={isDisabled}
                        />
                      )}
                    </div>
                    <div className='report-options-form__field100'>
                      {formConfig.filters.showCreditsOnly && (
                        <Form.Checkbox label='Credits only' name='isCreditsOnly' color={'blue'} disabled={isDisabled} />
                      )}
                      {formConfig.filters.showFuelPay && (
                        <Form.Checkbox label='FuelPay' name='isFuelPay' color={'blue'} disabled={isDisabled} />
                      )}
                      {formConfig.filters.showActiveVehicles && (
                        <Form.Checkbox
                          label='Active Vehicles'
                          name='isActiveVehicles'
                          color={'blue'}
                          disabled={isDisabled}
                        />
                      )}
                      {formConfig.filters.showTransaction10PM5AM && (
                        <Form.Checkbox
                          label='Transactions Between 10PM and 5AM'
                          name='transaction10PM5AM'
                          color={'blue'}
                          disabled={isDisabled}
                        />
                      )}
                    </div>
                  </>
                )}
                {formConfig.filters.showProductsSection && (
                  <>
                    <div className='report-options-form__filter-sub-heading'>Products and Services</div>
                    <div className='report-options-form__field100'>
                      <Form.Checkbox label='All Products' name='allProducts' color={'blue'} disabled={isDisabled} />
                    </div>
                    <div className='report-options-form__fields report-options-form__fields--3cols'>
                      {availableProducts.map((product) => (
                        <Form.Checkbox
                          key={product.value}
                          color={'blue'}
                          label={product.label}
                          name={product.value}
                          disabled={isDisabled}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {outputFormats && (
              <div className='report-options-form__form-section report-options-form__form-section--full report-options-form__form-section--createschedule'>
                <h3>File Format Type</h3>
                <Form.RadioGroup
                  label={'File Format Type'}
                  labelType={'hidden'}
                  color='black'
                  name='outputFormat'
                  radioType='radio'
                  disabledType='radio'
                  disabled={isDisabled}
                  values={outputFormats}
                />
              </div>
            )}
            <div className='report-options-form__form-section report-options-form__form-section--full'>
              <h3>Email Attachment</h3>
              <Form.Input label={'To:'} name={'emailTo'} disabled={isDisabled} />
              <Form.Input label={'Cc:'} name={'emailCc'} disabled={isDisabled} autoComplete={'do-not-autofill'} />
              <Form.Input
                label={'Subject:'}
                name={'emailSubject'}
                disabled={isDisabled}
                autoComplete={'do-not-autofill'}
              />
              <Form.TextArea label='Body:' name='emailBody' rows={3} disabled={isDisabled} />
            </div>
            <Form.Condition fieldName='createSchedule' condition={(c) => c === 'yes'}>
              <div className='report-options-form__form-section'>
                <h3>Schedule Options</h3>
                <Form.RadioGroup
                  label='Schedule Options'
                  labelType={'hidden'}
                  color='black'
                  name='scheduleFrequency'
                  radioType='radio'
                  direction='column'
                  disabledType='radio'
                  disabled={isDisabled}
                  values={[
                    {
                      label: 'Daily',
                      value: 'Daily',
                      tooltip: 'Delivered Monday-Sunday',
                    },
                    {
                      label: 'Weekly',
                      value: 'Weekly',
                      tooltip: 'Delivered weekly on specified day',
                      children: (
                        <Form.Condition fieldName='scheduleFrequency' condition={(c) => c === 'Weekly'}>
                          <Form.DropDown
                            name='reportingDayOfWeek'
                            label='Day of week'
                            labelType='hidden'
                            style={{ marginTop: 0 }}
                            disabled={isDisabled}
                            options={[
                              {
                                label: 'Monday',
                                value: 'Monday',
                              },
                              {
                                label: 'Tuesday',
                                value: 'Tuesday',
                              },
                              {
                                label: 'Wednesday',
                                value: 'Wednesday',
                              },
                              {
                                label: 'Thursday',
                                value: 'Thursday',
                              },
                              {
                                label: 'Friday',
                                value: 'Friday',
                              },
                            ]}
                          />
                        </Form.Condition>
                      ),
                    },
                    {
                      label: 'Monthly',
                      value: 'Monthly',
                      tooltip: 'Delivered at the start of the following month',
                    },
                  ]}
                />
              </div>
            </Form.Condition>
            {formConfig?.showReportingPeriod && (
              <Form.Condition fieldName='createSchedule' condition={(c) => c === 'yes'}>
                <div className='report-options-form__form-section'>
                  <h3>Schedule Reporting Period</h3>
                  <Form.RadioGroup
                    label='Schedule Reporting Period'
                    labelType={'hidden'}
                    color='black'
                    name='scheduleReportingPeriod'
                    radioType='radio'
                    direction='column'
                    disabledType='radio'
                    disabled={isDisabled}
                    values={
                      formConfig.filters.showMonthToDateCardLimit
                        ? [{ label: 'Calendar Month To Date', value: 'CalendarMonthToDate' }]
                        : scheduleReportingPeriodOptions &&
                          scheduleReportingPeriodOptions
                            .filter((opt) => opt.scheduleFrequency.some((frq) => frq === scheduleFrequency))
                            .map(({ label, value }) => ({ label, value }))
                    }
                  />
                </div>
              </Form.Condition>
            )}
          </div>
        </Form>
      )}
    </>
  );
};

export default ReportOptionsForm;
